// src/features/property-templates/templatesSlice.ts
import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '@/store/root-state';
import type { JsonRecord } from '@/domain/entities/property-schema';
import type {
  PropertyTemplate,
  TemplateAudience,
  TemplateScope,
  TemplateStatus,
} from '@/domain/entities/preperty-template';

import {
  fetchPropertyTemplates,
  fetchTemplateById,
  createPropertyTemplate,
  updatePropertyTemplate,
  deleteTemplate,
  publishTemplate,
  deprecateTemplate,
  cloneTemplate,
} from './propertyTemplatesThunks';
import {
  makeTemplateListScopeKey,
  type TemplateListScopeKey,
  type TemplateOrgScope,
  type TemplateSortDir,
  type TemplateSortKey,
  type UpsertPayload,
} from './propertyTemplatesTypes';

type StatusMap<T> = Record<TemplateStatus | 'all', T>;
type AudienceKey = TemplateAudience | 'all';
type ScopeKey = TemplateScope | 'all';

export interface PropertyTemplatesState {
  itemsByStatus: StatusMap<PropertyTemplate[]>;
  hasFetchedByStatus: StatusMap<boolean>;
  isListingByStatus: StatusMap<boolean>;
  errorByStatus: StatusMap<string | null>;
  listScopeByStatus: StatusMap<TemplateListScopeKey | null>;

  activeStatus: TemplateStatus | 'all';
  activeAudience: AudienceKey;
  activeScope: ScopeKey;
  activeOrgScope: TemplateOrgScope;

  sortKey: TemplateSortKey;
  sortDir: TemplateSortDir;

  selected: PropertyTemplate | null;

  isLoadingOne: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  isPublishing: boolean;
  isDeprecating: boolean;
  isCloning: boolean;

  hasChanges: boolean;
}

const initialState: PropertyTemplatesState = {
  itemsByStatus: { all: [], draft: [], published: [], deprecated: [] },
  hasFetchedByStatus: { all: false, draft: false, published: false, deprecated: false },
  isListingByStatus: { all: false, draft: false, published: false, deprecated: false },
  errorByStatus: { all: null, draft: null, published: null, deprecated: null },
  listScopeByStatus: { all: null, draft: null, published: null, deprecated: null },

  activeStatus: 'draft',
  activeAudience: 'all',
  activeScope: 'all',
  activeOrgScope: 'active-org',
  selected: null,

  sortKey: 'updatedAt',
  sortDir: 'desc',

  isLoadingOne: false,
  isSaving: false,
  isDeleting: false,
  isPublishing: false,
  isDeprecating: false,
  isCloning: false,

  hasChanges: false,
};

const sameId = (a: Pick<PropertyTemplate, 'id'>, b: Pick<PropertyTemplate, 'id'>) => a.id === b.id;

const upsertIntoBucket = (items: PropertyTemplate[], rec: PropertyTemplate): PropertyTemplate[] => {
  const idx = items.findIndex((x) => sameId(x, rec));
  if (idx === -1) return [rec, ...items];
  const copy = items.slice();
  copy[idx] = rec;
  return copy;
};

const removeFromBucket = (items: PropertyTemplate[], id: string): PropertyTemplate[] =>
  items.filter((x) => x.id !== id);

function applyUpsertResponse(state: PropertyTemplatesState, action: { payload: unknown }) {
  const payload = action.payload as UpsertPayload;
  const rec = payload.data;
  if (!rec) return;

  state.selected = rec;

  (['draft', 'published', 'deprecated'] as TemplateStatus[]).forEach((s) => {
    state.itemsByStatus[s] = removeFromBucket(state.itemsByStatus[s], rec.id);
  });

  state.itemsByStatus[rec.status] = upsertIntoBucket(state.itemsByStatus[rec.status], rec);
  state.hasChanges = false;
}

export const propertyTemplatesSlice = createSlice({
  name: 'propertyTemplates',
  initialState,
  reducers: {
    // ✅ renamed to avoid collisions with schemasSlice
    setTemplatesActiveStatus: (state, action: PayloadAction<TemplateStatus | 'all'>) => {
      state.activeStatus = action.payload;
    },

    setPropertyTemplatesActiveAudience: (state, action: PayloadAction<TemplateAudience | 'all'>) => {
      state.activeAudience = action.payload;
    },

    setPropertyTemplatesActiveScope: (state, action: PayloadAction<TemplateScope | 'all'>) => {
      state.activeScope = action.payload;
    },

    setPropertyTemplatesOrgScope: (state, action: PayloadAction<TemplateOrgScope>) => {
      state.activeOrgScope = action.payload;
    },

    setPropertyTemplatesSort: (state, action: PayloadAction<TemplateSortKey>) => {
      const nextKey = action.payload;

      if (state.sortKey !== nextKey) {
        state.sortKey = nextKey;
        state.sortDir = 'asc'; // or 'desc' if you want new columns default descending
        return;
      }

      state.sortDir = state.sortDir === 'asc' ? 'desc' : 'asc';
    },

    clearSelectedPropertyTemplate: (state) => {
      state.selected = null;
      state.hasChanges = false;
    },

    setSelectedPropertyTemplate: (state, action: PayloadAction<PropertyTemplate | null>) => {
      state.selected = action.payload;
      state.hasChanges = false;

      if (action.payload) {
        const rec = action.payload;
        state.itemsByStatus[rec.status] = upsertIntoBucket(state.itemsByStatus[rec.status], rec);
      }
    },

    patchSelectedPropertyTemplateMeta: (
      state,
      action: PayloadAction<{
        title?: PropertyTemplate['title'];
        description?: PropertyTemplate['description'];
        status?: PropertyTemplate['status'];
      }>,
    ) => {
      if (!state.selected) return;

      const nextStatus = action.payload.status ?? state.selected.status;
      const id = state.selected.id;
      const updatedAt = Math.floor(Date.now() / 1000);

      const next: PropertyTemplate = {
        ...state.selected,
        title: action.payload.title ?? state.selected.title,
        description: action.payload.description ?? state.selected.description,
        status: nextStatus,
        updatedAt,
      };

      if (nextStatus !== state.selected.status) {
        state.itemsByStatus[state.selected.status] = removeFromBucket(state.itemsByStatus[state.selected.status], id);
      }

      state.itemsByStatus[nextStatus] = upsertIntoBucket(state.itemsByStatus[nextStatus], next);
      state.selected = next;
      state.hasChanges = true;
    },

    setSelectedPropertyTemplateSchemaRef: (state, action: PayloadAction<PropertyTemplate['schemaRef']>) => {
      if (!state.selected) return;

      const updated: PropertyTemplate = {
        ...state.selected,
        schemaRef: action.payload,
        updatedAt: Math.floor(Date.now() / 1000),
      };

      state.itemsByStatus[updated.status] = upsertIntoBucket(state.itemsByStatus[updated.status], updated);
      state.selected = updated;
      state.hasChanges = true;
    },

    setSelectedPropertyTemplateData: (state, action: PayloadAction<JsonRecord>) => {
      if (!state.selected) return;

      const updated: PropertyTemplate = {
        ...state.selected,
        data: action.payload,
        updatedAt: Math.floor(Date.now() / 1000),
      };

      state.itemsByStatus[updated.status] = upsertIntoBucket(state.itemsByStatus[updated.status], updated);
      state.selected = updated;
      state.hasChanges = true;
    },

    createNewPropertyTemplateDraft: (
      state,
      action: PayloadAction<{ orgID: string; schemaRef?: PropertyTemplate['schemaRef'] }>,
    ) => {
      const ts = Math.floor(Date.now() / 1000);

      const rec: PropertyTemplate = {
        id: 'TEMPLATE#new',
        scope: 'organization',
        orgID: action.payload.orgID,
        audience: 'business',
        status: 'draft',
        schemaRef: action.payload.schemaRef ?? { schemaId: '', version: '' },
        title: '',
        description: '',
        data: {},
        createdAt: ts,
        updatedAt: ts,
        typeName: 'PropertyTemplate',
      };

      state.selected = rec;
      state.hasChanges = false;
    },

    markPropertyTemplatesClean: (state) => {
      state.hasChanges = false;
    },

    setPropertyTemplatesListForStatus: (
      state,
      action: PayloadAction<{ status: TemplateStatus; items: PropertyTemplate[] }>,
    ) => {
      const { status, items } = action.payload;
      state.itemsByStatus[status] = items;
      state.hasFetchedByStatus[status] = true;
      state.errorByStatus[status] = null;
    },

    setPropertyTemplatesErrorForStatus: (
      state,
      action: PayloadAction<{ status: TemplateStatus; error: string | null }>,
    ) => {
      const { status, error } = action.payload;
      state.errorByStatus[status] = error;
    },

    removePropertyTemplateById: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;

      (['draft', 'published', 'deprecated'] as TemplateStatus[]).forEach((s) => {
        state.itemsByStatus[s] = removeFromBucket(state.itemsByStatus[s], id);
      });

      if (state.selected?.id === id) {
        state.selected = null;
        state.hasChanges = false;
      }
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchPropertyTemplates.pending, (state, action) => {
        const status = action.meta.arg.status;
        state.isListingByStatus[status] = true;
        state.errorByStatus[status] = null;
      })
      .addCase(fetchPropertyTemplates.fulfilled, (state, action) => {
        const status = action.meta.arg.status;
        state.isListingByStatus[status] = false;
        state.itemsByStatus[status] = action.payload.templates;
        state.hasFetchedByStatus[status] = true;
        state.listScopeByStatus[status] = makeTemplateListScopeKey(
          action.meta.arg.orgID,
          action.meta.arg.audience ?? 'all',
        );
      })
      .addCase(fetchPropertyTemplates.rejected, (state, action) => {
        const status = action.meta.arg.status;
        state.isListingByStatus[status] = false;
        state.hasFetchedByStatus[status] = true;
        state.errorByStatus[status] =
          (action.payload as { message?: string } | undefined)?.message ??
          action.error.message ??
          'Failed to fetch templates';
      })

      .addCase(fetchTemplateById.pending, (state) => {
        state.isLoadingOne = true;
      })
      .addCase(fetchTemplateById.fulfilled, (state, action) => {
        state.isLoadingOne = false;

        const rec = action.payload;
        state.selected = rec;

        (['draft', 'published', 'deprecated'] as TemplateStatus[]).forEach((s) => {
          state.itemsByStatus[s] = removeFromBucket(state.itemsByStatus[s], rec.id);
        });

        state.itemsByStatus[rec.status] = upsertIntoBucket(state.itemsByStatus[rec.status], rec);
        state.hasChanges = false;
      })
      .addCase(fetchTemplateById.rejected, (state) => {
        state.isLoadingOne = false;
      })

      .addCase(createPropertyTemplate.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(createPropertyTemplate.fulfilled, (state, action) => {
        state.isSaving = false;
        applyUpsertResponse(state, action);
      })
      .addCase(createPropertyTemplate.rejected, (state) => {
        state.isSaving = false;
      })

      .addCase(updatePropertyTemplate.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(updatePropertyTemplate.fulfilled, (state, action) => {
        state.isSaving = false;
        applyUpsertResponse(state, action);
      })
      .addCase(updatePropertyTemplate.rejected, (state) => {
        state.isSaving = false;
      })

      .addCase(deleteTemplate.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        state.isDeleting = false;

        const id = action.meta.arg.id;

        (['draft', 'published', 'deprecated'] as TemplateStatus[]).forEach((s) => {
          state.itemsByStatus[s] = removeFromBucket(state.itemsByStatus[s], id);
        });

        if (state.selected?.id === id) {
          state.selected = null;
          state.hasChanges = false;
        }
      })
      .addCase(deleteTemplate.rejected, (state, action) => {
        state.isDeleting = false;

        const status = state.activeStatus;
        state.errorByStatus[status] =
          (action.payload as { message?: string } | undefined)?.message ??
          action.error.message ??
          'Failed to delete template';
      })

      .addCase(publishTemplate.pending, (state) => {
        state.isPublishing = true;
        state.errorByStatus.draft = null;
      })
      .addCase(publishTemplate.fulfilled, (state) => {
        state.isPublishing = false;
        state.hasFetchedByStatus.published = false;
      })
      .addCase(publishTemplate.rejected, (state, action) => {
        state.isPublishing = false;
        state.errorByStatus.draft =
          (action.payload as { message?: string } | undefined)?.message ??
          action.error.message ??
          'Failed to publish template';
      })

      .addCase(deprecateTemplate.pending, (state) => {
        state.isDeprecating = true;
        state.errorByStatus.published = null;
      })
      .addCase(deprecateTemplate.fulfilled, (state) => {
        state.isDeprecating = false;
        state.hasFetchedByStatus.published = false;
        state.hasFetchedByStatus.deprecated = false;
      })
      .addCase(deprecateTemplate.rejected, (state, action) => {
        state.isDeprecating = false;
        state.errorByStatus.published =
          (action.payload as { message?: string } | undefined)?.message ??
          action.error.message ??
          'Failed to deprecate template';
      })

      .addCase(cloneTemplate.pending, (state) => {
        state.isCloning = true;
        state.errorByStatus.published = null;
      })
      .addCase(cloneTemplate.fulfilled, (state, action) => {
        state.isCloning = false;

        const payload = action.payload as UpsertPayload;
        const rec = payload.data;

        if (rec) {
          state.selected = rec;
          state.itemsByStatus.draft = upsertIntoBucket(state.itemsByStatus.draft, rec);
          state.hasFetchedByStatus.draft = false;
          state.hasChanges = false;
        }
      })
      .addCase(cloneTemplate.rejected, (state, action) => {
        state.isCloning = false;
        state.errorByStatus.published =
          (action.payload as { message?: string } | undefined)?.message ??
          action.error.message ??
          'Failed to clone template';
      });
  },
});

// ✅ renamed exports to avoid collisions across slices
export const {
  setTemplatesActiveStatus,
  clearSelectedPropertyTemplate,
  setSelectedPropertyTemplate,
  patchSelectedPropertyTemplateMeta,
  setSelectedPropertyTemplateSchemaRef,
  setSelectedPropertyTemplateData,
  createNewPropertyTemplateDraft,
  markPropertyTemplatesClean,
  setPropertyTemplatesListForStatus,
  setPropertyTemplatesErrorForStatus,
  removePropertyTemplateById,
  setPropertyTemplatesActiveAudience,
  setPropertyTemplatesActiveScope,
  setPropertyTemplatesOrgScope,
  setPropertyTemplatesSort,
} = propertyTemplatesSlice.actions;

// ----------------- selectors (renamed) -----------------
const selectPropertyTemplatesState = (state: RootState) => state.propertyTemplates;

export const selectPropertyTemplatesForActiveStatus = createSelector(
  [selectPropertyTemplatesState],
  (s) => s.itemsByStatus[s.activeStatus],
);

export const selectActiveAudience = createSelector([selectPropertyTemplatesState], (s) => s.activeAudience);

export const selectActiveScope = createSelector([selectPropertyTemplatesState], (s) => s.activeScope);

export const selectActiveOrgScope = createSelector([selectPropertyTemplatesState], (s) => s.activeOrgScope);

export const selectDraftPropertyTemplates = createSelector(
  [selectPropertyTemplatesState],
  (s) => s.itemsByStatus.draft,
);

export const selectPropertyTemplateById = createSelector(
  [(state: RootState) => state.propertyTemplates, (_: RootState, id?: string | null) => id],
  (templates, id) => {
    if (!id) return null;
    if (templates.selected && templates.selected.id === id) return templates.selected;

    const buckets = templates.itemsByStatus;
    const all = [...buckets.draft, ...buckets.published, ...buckets.deprecated];
    return all.find((t) => t.id === id) ?? null;
  },
);

export const selectSortedFilteredPropertyTemplates = createSelector(
  [
    (state: RootState) => state.propertyTemplates.itemsByStatus,
    (state: RootState) => state.propertyTemplates.activeStatus,
    (state: RootState) => state.propertyTemplates.activeAudience,
    (state: RootState) => state.propertyTemplates.activeScope,
    (state: RootState) => state.propertyTemplates.sortKey,
    (state: RootState) => state.propertyTemplates.sortDir,
  ],
  (itemsByStatus, activeStatus, activeAudience, activeScope, sortKey, sortDir) => {
    const bucket = itemsByStatus[activeStatus] ?? [];

    // 1) filter by audience
    const audienceFiltered =
      activeAudience === 'all' ? bucket : bucket.filter((template) => template.audience === activeAudience);
    const filtered =
      activeScope === 'all' ? audienceFiltered : audienceFiltered.filter((template) => template.scope === activeScope);

    // 2) sort
    const copy = filtered.slice();

    const cmpString = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });
    const cmpNumber = (a: number, b: number) => a - b;

    copy.sort((A, B) => {
      let res = 0;

      if (sortKey === 'title') res = cmpString(A.title ?? '', B.title ?? '');
      else if (sortKey === 'status') res = cmpString(A.status ?? '', B.status ?? '');
      else if (sortKey === 'audience') res = cmpString(A.audience ?? '', B.audience ?? '');
      else if (sortKey === 'updatedAt') res = cmpNumber(A.updatedAt ?? 0, B.updatedAt ?? 0);

      return sortDir === 'asc' ? res : -res;
    });

    return copy;
  },
);

export default propertyTemplatesSlice.reducer;
